/**
 * KanbanQuoteCardFooter — the CRM-QUOTE-PIPELINE structured quote face.
 *
 * Rendered for crm-quote cards (provider === 'crm-quote') in EVERY density.
 * Operator (2026-06-27, comparing the old bespoke pipeline) asked for the
 * OLD card's useful STRUCTURE back: a clean stack of the facts you scan a
 * quote funnel on, not a thin self-suppressing chip row. So this is a small
 * structured block, mirroring that card:
 *
 *   ┌────────────────────────────────────┐
 *   │ 🏢 Client / company        (line 1) │ ← quote_meta_json.client
 *   │ $162,610.01            75%  (line 2) │ ← amount_cents (HEADLINE) + win%
 *   │ 👤 Estimator         ⚠ Chase (line 3)│ ← quote_meta_json.estimator + chase/age
 *   └────────────────────────────────────┘
 *
 * The $ value line ALWAYS renders for a quote card — an unpriced quote shows a
 * muted "$0.00" (meaningful: "needs pricing"), exactly like the old card, so a
 * quote tile is never empty. Client / estimator are presence-gated (populated
 * by the BE feeder's build_quote_meta → quote_meta_json; backfilled by the
 * QUOTE_PIPELINE beat / a quote edit). All derived signals are pure
 * (utils/quoteCardSignals, utils/quoteLaneValue); this file is display only.
 * Comments / attachments / due live in the generic KanbanCardFooter glyph strip
 * below — this block owns the quote-specific facts.
 */
import { Building2, Clock, Flame, Percent, UserRound } from 'lucide-react'

import type { Card } from '../types'
import { formatFullMoney } from '../utils/quoteLaneValue'
import { daysInLane, quoteChaseLevel } from '../utils/quoteCardSignals'

interface KanbanQuoteCardFooterProps {
  card: Card
  /** The current lane's kind (e.g. 'QUOTE_SUBMITTED'); drives the chase pip. */
  laneKind?: string
  /**
   * Compact density (the board's production default). Keeps the structured
   * stack (client / $ / estimator + chase) but trims the lowest-signal age
   * chip so a tight card stays calm. The chase pip ALWAYS shows — it is the
   * "follow up now" signal and must survive compaction.
   */
  compact?: boolean
}

export function KanbanQuoteCardFooter({ card, laneKind, compact = false }: KanbanQuoteCardFooterProps) {
  const cents =
    typeof card.amount_cents === 'number' && card.amount_cents > 0 ? card.amount_cents : 0
  const winRaw = typeof card.win_pct === 'number' ? card.win_pct : null
  const win = winRaw == null ? null : winRaw <= 0 ? 0 : winRaw >= 100 ? 100 : Math.round(winRaw)
  const ageDays = daysInLane(card.lane_entered_at)
  const chase = quoteChaseLevel(laneKind, ageDays)

  // First-class identity, mirrored from report.Quote by the feeder. Each is
  // independently presence-gated — a quote with a client but no estimator
  // (or vice-versa) shows only what it has.
  const meta = card.quote_meta_json ?? null
  const client = (meta?.client ?? '').trim() || null
  const estimator = (meta?.estimator ?? '').trim() || null

  // The headline $ ALWAYS shows for a quote card — an unpriced quote reads as a
  // muted "$0.00" (a real signal: it needs pricing) rather than vanishing.
  const moneyText = cents > 0 ? formatFullMoney(cents) : '$0.00'
  const moneyMuted = cents === 0

  // The meta row carries the estimator + the chase/age signal. Render it when
  // there's something to say there: an estimator, the chase pip (any density),
  // or — expanded only — a stale flag / plain age.
  const showAge = !compact && ageDays != null
  const showMetaRow = !!estimator || chase === 'chase' || (showAge && chase !== 'none') || showAge

  return (
    <div
      data-testid="kanban-quote-card-footer"
      data-chase={chase !== 'none' ? chase : undefined}
      className={['mt-1.5', compact ? 'space-y-0.5' : 'space-y-1'].join(' ')}
    >
      {client && (
        <div
          data-testid="kanban-quote-client-chip"
          className="flex items-center gap-1 text-xs font-medium text-neutral-700 dark:text-neutral-200"
          title={`Client: ${client}`}
        >
          <Building2 className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
          <span className="truncate">{client}</span>
        </div>
      )}

      {/* Headline value (always) + win% — the two numbers an operator triages a
          quote on, on one baseline-aligned row. */}
      <div className="flex items-baseline justify-between gap-2">
        <span
          data-testid="kanban-quote-value"
          className={[
            'text-[15px] font-bold leading-none tabular-nums',
            moneyMuted
              ? 'text-neutral-400 dark:text-neutral-500'
              : 'text-neutral-900 dark:text-neutral-50',
          ].join(' ')}
          title={moneyMuted ? 'No quote value yet — needs pricing' : `Quote value: ${moneyText}`}
        >
          {moneyText}
        </span>
        {win != null && (
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
            title={`Estimated win probability: ${win}%`}
          >
            <Percent className="h-2.5 w-2.5" aria-hidden />
            {win}
          </span>
        )}
      </div>

      {showMetaRow && (
        <div className="flex items-center justify-between gap-2 text-[11px] text-neutral-500 dark:text-neutral-400">
          {estimator ? (
            <span
              data-testid="kanban-quote-estimator-chip"
              className="inline-flex min-w-0 items-center gap-1"
              title={`Estimator: ${estimator}`}
            >
              <UserRound className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
              <span className="truncate">{estimator}</span>
            </span>
          ) : (
            <span aria-hidden />
          )}

          {chase === 'chase' ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 font-semibold text-red-600 dark:text-red-400"
              title={`Chase — silent ${ageDays} days in this stage`}
            >
              <Flame className="h-3 w-3" aria-hidden />
              Chase{ageDays != null ? ` ${ageDays}d` : ''}
            </span>
          ) : showAge ? (
            <span
              className={[
                'inline-flex shrink-0 items-center gap-1',
                chase === 'stale' ? 'font-medium text-amber-600 dark:text-amber-400' : '',
              ].join(' ')}
              title={
                chase === 'stale'
                  ? `Stale — sat ${ageDays} days in this stage`
                  : `In this stage for ${ageDays} day${ageDays === 1 ? '' : 's'}`
              }
            >
              <Clock className="h-3 w-3 opacity-70" aria-hidden />
              {ageDays}d{chase === 'stale' ? ' · stale' : ''}
            </span>
          ) : null}
        </div>
      )}
    </div>
  )
}
